import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserDialogComponent } from './user-dialog.component';
import { User } from '../../models/user';
import { UserService } from '../../services/user.service';
import { AuthService } from '../../services/auth.service';
import { ResponsiveService } from '../../core/responsive.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'], // Updated to .scss
  standalone : false
})
export class UsersComponent implements OnInit, OnDestroy {
  users: User[] = [];
  filteredUsers: User[] = [];
  displayedColumns: string[] = ['name', 'nickname', 'email', 'administrator', 'active', 'actions']; // Default desktop columns
  selectedUser: User | null = null;
  currentUserIsAdmin: boolean = false;
  isHandset$!: Observable<boolean>;
  private destroy$ = new Subject<void>();

  constructor(
  private dialog: MatDialog,
  private userService: UserService,
  private authService: AuthService,
  private responsive: ResponsiveService
  ) {}

  ngOnInit(): void {
    if (!this.authService.isCurrentUserAdmin()) {
      throw new Error('Access denied: Users component is only available to admins.');
    }
    this.loadUsers();
    // Default desktop columns already set above
    // Hook responsive layout: handset only shows Name + Email
    this.isHandset$ = this.responsive.isHandset$;
    this.isHandset$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isHandset => {
        this.displayedColumns = isHandset
          ? ['name', 'email']
          : ['name', 'nickname', 'email', 'administrator', 'active', 'actions'];
      });
    // Try to get the current user from AuthService (which should read from localStorage)
    setTimeout(() => {
      const currentUser = this.authService.getCurrentUser();
      this.currentUserIsAdmin = this.authService.isCurrentUserAdmin();

    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadUsers(): void {
    this.userService.getUsers().subscribe(data => {
      this.users = data;
      this.filteredUsers = data;
    });
  }

  viewUser(user: User): void {
    this.dialog.open(UserDialogComponent, {
      data: { user, viewMode: true },
      width: '1000px', // Explicitly set the dialog width
      maxWidth: '90vw' // Optional: Ensure responsiveness on smaller screens
    }).afterClosed().subscribe(() => {
      this.selectedUser = null; // Cleanup after dialog closes
    });
  }

  editUser(user: User): void {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: { user, viewMode: false },
      width: '600px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadUsers();
    });
  }

  addUser(): void {
    const dialogRef = this.dialog.open(UserDialogComponent, {
      data: { user: null, viewMode: false },
      width: '600px',
      maxWidth: '90vw'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadUsers();
    });
  }

  deleteUser(userId: number): void {
    if (confirm('Are you sure you want to delete this user?')) {
      this.userService.deleteUser(userId).subscribe(() => {
        this.loadUsers(); // Reload users after deletion
      });
    }
  }
}